import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Loader2,
  Info,
  ChevronDown,
  ChevronUp,
  CheckCircle2
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField, Select } from '../ui/Form';
import { accountsApi } from '../../services/api/endpoints/accounts';
import { useAuthStore } from '../../stores/authStore';
import type { 
  ChartOfAccountsInfo, 
  AccountingFramework,
  InitializationResult 
} from '../../types/account-initialization';
import type { FiscalYear } from '../../types/fiscal-year';
import toast from 'react-hot-toast';

interface InitializeAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  fiscalYear: FiscalYear;
  onSuccess?: (result: InitializationResult) => void;
}

/**
 * Modal pour initialiser les comptes d'un exercice fiscal
 */
export function InitializeAccountsModal({
  isOpen,
  onClose,
  fiscalYear,
  onSuccess
}: InitializeAccountsModalProps) {
  const [charts, setCharts] = useState<ChartOfAccountsInfo[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<AccountingFramework>('SYSCOHADA_NORMAL');
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [postableOnly, setPostableOnly] = useState(false);
  const [result, setResult] = useState<InitializationResult | null>(null);

  const { organization } = useAuthStore();

  // Charger les plans comptables disponibles
  useEffect(() => {
    const loadCharts = async () => {
      try {
        setLoading(true);
        const response = await accountsApi.getAvailableCharts();
        
        if (response.success && response.data) {
          const chartsData = Array.isArray(response.data) 
            ? response.data 
            : (response.data as any).data || [];
          setCharts(chartsData);
        } else {
          // Fallback
          setCharts([
            {
              framework: 'SYSCOHADA_NORMAL',
              name: 'SYSCOHADA - Système Normal',
              description: 'Plan comptable complet pour les entreprises avec CA ≥ 100M FCFA',
              accountCount: 491
            },
            {
              framework: 'SYSCOHADA_MINIMAL',
              name: 'SYSCOHADA - Système Minimal',
              description: 'Plan comptable simplifié pour les TPE avec CA < 100M FCFA',
              accountCount: 147
            },
            {
              framework: 'IFRS',
              name: 'IFRS - International',
              description: 'Plan comptable basé sur les normes IFRS internationales',
              accountCount: 225
            }
          ]);
        }
      } catch (err) {
        console.error('Error loading charts:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadCharts();
      setResult(null); // Reset result when opening
    }
  }, [isOpen]);

  const handleInitialize = async () => {
    if (!organization?.id) {
      toast.error('Organisation non trouvée');
      return;
    }

    try {
      setInitializing(true);
      
      const response = await accountsApi.initializeAccounts({
        fiscalYearId: fiscalYear.id,
        companyId: organization.id,
        framework: selectedFramework,
        postableOnly,
        overwrite: false
      });

      if (response.success && response.data) {
        const initResult = (response.data as any).data || response.data;
        setResult(initResult);
        toast.success(`${initResult.accountsCreated} comptes initialisés avec succès !`);
        onSuccess?.(initResult);
      } else {
        toast.error((response as any).error || 'Erreur lors de l\'initialisation');
      }
    } catch (err) {
      console.error('Error initializing:', err);
      toast.error('Erreur lors de l\'initialisation des comptes');
    } finally {
      setInitializing(false);
    }
  };

  const selectedChart = charts.find(c => c.framework === selectedFramework);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={result ? 'Initialisation terminée' : `Initialiser les comptes - ${fiscalYear.code}`}
    >
      <div className="space-y-6">
        {result ? (
          // Affichage du résultat
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              <div>
                <p className="font-medium text-green-700 dark:text-green-300">
                  Initialisation réussie !
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {result.message}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Comptes créés</p>
                <p className="text-2xl font-bold">{result.accountsCreated}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Plan comptable</p>
                <p className="text-lg font-medium">{result.framework}</p>
              </div>
            </div>

            {result.stats && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">Répartition par classe</p>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(result.stats.byClass).map(([classNum, count]) => (
                    <div key={classNum} className="text-center p-2 bg-background rounded">
                      <p className="text-xs text-muted-foreground">Classe {classNum}</p>
                      <p className="font-medium">{count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={onClose}>
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          // Formulaire d'initialisation
          <>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  L'initialisation créera tous les comptes du plan comptable sélectionné 
                  pour l'exercice <strong>{fiscalYear.code}</strong>.
                </p>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-4">
                <FormField label="Plan comptable">
                  <Select
                    value={selectedFramework}
                    onChange={(e) => setSelectedFramework(e.target.value as AccountingFramework)}
                    options={charts.map(chart => ({
                      value: chart.framework,
                      label: `${chart.name} (${chart.accountCount} comptes)`
                    }))}
                    disabled={initializing}
                  />
                </FormField>

                {selectedChart && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      {selectedChart.description}
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  Options avancées
                </button>

                {showAdvanced && (
                  <div className="pl-4 border-l-2 border-border">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={postableOnly}
                        onChange={(e) => setPostableOnly(e.target.checked)}
                        className="rounded"
                        disabled={initializing}
                      />
                      <span className="text-sm">
                        Créer uniquement les comptes postables (sans les comptes parents)
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="ghost" onClick={onClose} disabled={initializing}>
                Annuler
              </Button>
              <Button 
                onClick={handleInitialize} 
                disabled={initializing || loading}
                icon={initializing ? Loader2 : Database}
              >
                {initializing ? 'Initialisation...' : 'Initialiser les comptes'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
